import type { Node } from 'reactflow';

/** Gap in pixels between the bottom of the lowest existing node and the top of the new one. */
const GAP = 80;

/**
 * Default node height used when React Flow has not yet measured a node.
 * React Flow v11 stores the measured height in `node.height`; before the
 * first render completes it is `undefined`.
 */
const DEFAULT_HEIGHT = 100;

/** Fixed origin used when the canvas is empty. */
const ORIGIN = { x: 100, y: 100 };

/**
 * Return the position for a new node so that it appears directly below the
 * bottom-most existing node, horizontally aligned with it.
 *
 * Rules:
 * - Empty canvas → the fixed origin { x: 100, y: 100 }.
 * - Otherwise → x of the bottom-most node, y = bottom-edge + GAP (80 px).
 * - A node whose `height` has not been measured yet is assumed to be
 *   DEFAULT_HEIGHT (100 px) tall.
 *
 * This replaces the previous `{ x: Math.random() * 300, y: Math.random() * 300 }`
 * placement that caused nodes to overlap each other and land under the minimap.
 */
export function nextNodePosition(nodes: Node[]): { x: number; y: number } {
  if (nodes.length === 0) return { ...ORIGIN };

  // Find the node whose bottom edge is furthest down the canvas.
  let lowestNode = nodes[0];
  let lowestBottom = lowestNode.position.y + (lowestNode.height ?? DEFAULT_HEIGHT);

  for (const n of nodes) {
    const bottom = n.position.y + (n.height ?? DEFAULT_HEIGHT);
    if (bottom > lowestBottom) {
      lowestBottom = bottom;
      lowestNode = n;
    }
  }

  return {
    x: lowestNode.position.x,
    y: lowestBottom + GAP,
  };
}
